import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user,            setUser]            = useState(null);
  const [token,           setToken]           = useState(localStorage.getItem('sms_token'));
  const [schoolSlug,      setSchoolSlug]      = useState(localStorage.getItem('sms_slug') || '');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('sms_token'));
  const [loading,         setLoading]         = useState(true);

  useEffect(() => {
    if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    else delete axios.defaults.headers.common['Authorization'];
  }, [token]);

  useEffect(() => {
    const storedToken = localStorage.getItem('sms_token');
    const storedUser  = localStorage.getItem('sms_user');
    const storedSlug  = localStorage.getItem('sms_slug') || '';
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        setSchoolSlug(storedSlug || parsedUser.school_slug || '');
        setIsAuthenticated(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch { logout(); }
    }
    setLoading(false);
  }, []);

  const login = async (username, password, school_slug) => {
    try {
      const endpoint = school_slug
        ? `${process.env.REACT_APP_API_URL}/platform/school/login`
        : `${process.env.REACT_APP_API_URL}/auth/login`;

      const res = await axios.post(endpoint,
        school_slug ? { username, password, school_slug } : { username, password }
      );

      if (res.data.success) {
        const { token, user } = res.data;
        const slug = school_slug || user.school_slug || '';

        localStorage.setItem('sms_token', token);
        localStorage.setItem('sms_user',  JSON.stringify(user));
        localStorage.setItem('sms_slug',  slug);

        setToken(token);
        setUser(user);
        setSchoolSlug(slug);
        setIsAuthenticated(true);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        return { success: true, must_change_password: user.must_change_password, school_slug: slug };
      }
      return { success: false, message: res.data.message };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Login failed' };
    }
  };

  const logout = () => {
    const slug = localStorage.getItem('sms_slug') || schoolSlug;
    localStorage.removeItem('sms_token');
    localStorage.removeItem('sms_user');
    localStorage.removeItem('sms_slug');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setSchoolSlug('');
    setIsAuthenticated(false);
    // Redirect to the school login page (not the generic /login)
    if (slug) {
      window.location.href = `/school/${slug}/login`;
    }
  };

  const can = (module, level = 'view') => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perm = user.permissions?.[module];
    if (!perm || perm.access === 'none') return false;
    if (level === 'full') return perm.access === 'full';
    return true;
  };

  const ownDataOnly = (module) => {
    if (!user || user.role === 'admin') return false;
    return !!user.permissions?.[module]?.own_data_only;
  };

  // Build a prefixed path for navigation: /school/slug/dashboard
  const schoolPath = (path) => {
    const s = schoolSlug || localStorage.getItem('sms_slug') || '';
    return s ? `/school/${s}${path}` : path;
  };

  return (
    <AuthContext.Provider value={{
      user, token, schoolSlug, isAuthenticated, loading,
      login, logout, can, ownDataOnly, schoolPath
    }}>
      {children}
    </AuthContext.Provider>
  );
};
